import styled from '@emotion/styled';
import { Variables } from '@enter/core';

export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
`;

export const Subtitle = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
    margin-top: 4px;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

/**
 * What a tab shows when it cannot show a table yet.
 *
 * It states what is missing and why rather than rendering an empty table. An empty table asserts
 * "no rejections"; this asserts "not groupable yet", which is the true statement while FE-73 · B1
 * is open.
 */
export const PendingPanel = styled.div`
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.nx8};
    border-radius: ${Variables.Rounded.medium};
    padding: 32px;
    width: 100%;
`;

export const PendingTitle = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 8px;
`;

export const PendingBody = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
    line-height: 1.6;
    max-width: 720px;
`;

/** Wrapper that carries the "why is this disabled" title a disabled button cannot show itself. */
export const ActionSlot = styled.span`
    display: inline-flex;
`;

/** The controls on the tab row, as the design sets them: search, the date range, Filters, Sort. */
export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

/**
 * The panel the tabs control. The page row cannot carry the tabpanel role itself (it passes on only a class
 * name), so this wraps its content and lays it out the way the row does: nothing on screen moves.
 */
export const TabPanel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

/** Where the search field sits in the toolbar. The field itself is the core `BaseInput`, compact, with its icon. */
export const SearchSlot = styled.div`
    flex: none;
    width: 234px;
`;

/**
 * The table's own header, as the design measures it: 12px by 16px inside the card, a rule underneath. The card has no
 * padding of its own (the table runs edge to edge), so everything else inside it has to bring its inset.
 */
export const SectionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    border-bottom: 1px solid ${Variables.Swatches.Gray3};
`;

export const SectionTitle = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 14px;
    font-weight: 600;
`;

export const SectionSub = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    margin-top: 2px;
`;

/** The table's controls, right of its title: the routine-adjustment filter, then Group by. */
export const SectionControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
`;

export const GroupBy = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

export const GroupByLabel = styled.span`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
`;

/** The design's table type size, set once, and its header row on the lightest grey. */
export const TableFrame = styled.div`
    font-size: 13px;

    .table-header-with-filter {
        background-color: ${Variables.Swatches.Gray1};
    }
`;

export const TableHeader = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const TableHeaderRight = styled(TableHeader)`
    width: 100%;
    text-align: right;
`;

export const TableCell = styled.div`
    color: ${Variables.Swatches.Gray9};
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/**
 * Reason text, a step lighter than the code beside it, as the design sets it — and wrapped, as the design has it:
 * a CARC sentence runs to a dozen words, and the row is worth the height if the whole sentence can be read.
 */
export const ReasonCell = styled(TableCell)`
    color: ${Variables.Swatches.Gray8};
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.45;
    padding: 4px 0;
`;

/**
 * The code, as the design's grey chip. Monospaced, because a code is read character by character and
 * `CO:16` must not look like `CO:161`. `Variables.Fonts` has only `calibre`, so the stack is spelled
 * out here — a font literal, not a colour one (NS-2 governs colour). Worth a `mono` token in the
 * design system if more code-bearing surfaces appear.
 */
export const CodeChip = styled.span`
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    background-color: ${Variables.Swatches.Gray2};
    color: ${Variables.Swatches.Gray11};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
`;

export const NumberCell = styled.div`
    color: ${Variables.Swatches.Gray9};
    font-size: 13px;
    width: 100%;
    text-align: right;
    font-variant-numeric: tabular-nums;
`;

/** The per-row worker action, right-aligned like the design's WORKER column. */
export const WorkerCell = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;

    button {
        white-space: nowrap;
    }
`;

/** A money cell the report cannot fill yet: a muted dash, with the reason on hover — never a zero. */
export const PendingCell = styled(NumberCell)`
    color: ${Variables.Swatches.Gray6};
    cursor: help;
`;

/** A text cell the report cannot fill yet (the remark description): a muted dash, with the reason on hover. */
export const PendingText = styled(TableCell)`
    color: ${Variables.Swatches.Gray6};
    cursor: help;
`;

export const Notice = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
    padding: 24px 16px;
`;

/** Names the columns the design has and this table does not, so their absence is legible. */
export const MissingColumns = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    line-height: 1.6;
    border-top: 1px solid ${Variables.Colors.nx8};
    padding: 12px 16px 16px;
`;
